import { usePage } from '@inertiajs/react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface TeamMembership {
  team_id: number
  team_name: string
  primary: boolean
}

// Reads the signed-in user's team memberships from Inertia shared props.
// `auth.user` is present on every page render via
// ApplicationController#inertia_share — including pages whose form data is
// fetched separately as JSON (e.g. the task create sheet), since the shared
// prop still rides on the page itself.
export function useTeamMemberships(): TeamMembership[] {
  const { props } = usePage()
  return props.auth.user?.memberships ?? []
}

// The team_id the owning-team picker defaults to: the user's primary
// membership. Empty string when there is no membership (the picker is hidden
// in that case and the backend files the record under the actor's primary
// team regardless).
export function defaultTeamId(memberships: TeamMembership[]): string {
  const primary = memberships.find((m) => m.primary) ?? memberships[0]
  return primary ? String(primary.team_id) : ''
}

interface TeamPickerProps {
  value: string
  onChange: (teamId: string) => void
  // Inertia useForm errors are typed as string[] (errorValueType in
  // globals.d.ts); the task sheet passes a plain string. Accept both.
  error?: string | string[]
  label?: string
  placeholder?: string
}

// Owning-team selector for multi-team users (US3 / #120). Renders nothing
// when the user belongs to one team or fewer: the single team is implied and
// the backend defaults the record to the actor's primary team. When shown,
// it lists only the actor's direct memberships — the create interaction
// (via CrmCore::TeamResolver) rejects any team the actor is not a member of.
export function TeamPicker({
  value,
  onChange,
  error,
  label = 'Équipe',
  placeholder = 'Choisir une équipe',
}: TeamPickerProps) {
  const memberships = useTeamMemberships()
  if (memberships.length <= 1) return null

  return (
    <div className="space-y-2">
      <Label>{label} *</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {memberships.map((m) => (
            <SelectItem key={m.team_id} value={String(m.team_id)}>
              {m.team_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
